import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiException } from './api';

/**
 * Hand-written client for the chart-builder endpoints.
 *
 * api.ts is NSwag-generated from the C# controllers and must never be edited by hand, but
 * regenerating it needs the API running at localhost:5001 to serve swagger.json. This file is the
 * stopgap: same constructor shape as a generated *Service, same axios instance, same ApiException.
 * When api.ts is next regenerated it will contain an AnalyticsService with these three methods —
 * at which point this file is deleted and provider.tsx points at the generated one instead.
 */

export enum ChartParameterRole {
  Measure = 0,
  Dimension = 1,
  Time = 2,
}

export interface ChartParameterDto {
  key: string;
  label: string;
  role: ChartParameterRole;
  /** Display unit ("m³", "kPa"). Null for dimensions and dates. */
  unit?: string | null;
}

export interface ChartParameterGroupDto {
  name: string;
  items: ChartParameterDto[];
}

/**
 * One flat row per pour. Property names are the catalog keys, so a spec holding "actualVolume" reads
 * row.actualVolume with no mapping. Dates arrive as ISO-8601 strings; numbers are raw and unrounded.
 */
export interface PourFactDto {
  stopeName?: string | null;
  level?: string | null;
  area?: string | null;
  stopeVolume?: number | null;

  pourReference?: string | null;
  status?: string | null;
  intendedPourDate?: string | null;
  plannedVolume?: number | null;
  targetUcs?: number | null;
  targetMassConcentration?: number | null;
  designBinderContent?: number | null;
  designThroughput?: number | null;

  completedDate?: string | null;
  actualVolume?: number | null;
  actualMassConcentration?: number | null;
  actualBinderContent?: number | null;
  dryTonnage?: number | null;
  totalBinder?: number | null;
  ucsAverage?: number | null;
  scheduleVarianceDays?: number | null;
}

export class AnalyticsService {
  private instance: AxiosInstance;

  private baseUrl: string;

  constructor(baseUrl: string, instance: AxiosInstance) {
    this.baseUrl = baseUrl;
    this.instance = instance;
  }

  getChartParameters(projectId: string): Promise<ChartParameterGroupDto[]> {
    return this.send<ChartParameterGroupDto[]>(
      'GET',
      `/api/projects/${encodeURIComponent(projectId)}/analytics/chart-parameters`,
    );
  }

  getPourFacts(projectId: string, scenarioId?: string | null): Promise<PourFactDto[]> {
    const query = scenarioId ? `?scenarioId=${encodeURIComponent(scenarioId)}` : '';
    return this.send<PourFactDto[]>(
      'GET',
      `/api/projects/${encodeURIComponent(projectId)}/analytics/pour-facts${query}`,
    );
  }

  /**
   * The project's saved chart specs as a raw JSON array string. Read here rather than off ProjectDto
   * because that type comes from the generated api.ts and cannot gain a property without a regen.
   */
  getDashboardCharts(projectId: string): Promise<string> {
    return this.send<string>(
      'GET',
      `/api/projects/${encodeURIComponent(projectId)}/analytics/charts`,
    );
  }

  /**
   * Persists the project's user-authored chart specs as a JSON array. Lives here rather than on
   * ProjectsService for the same reason as the rest of this file — it is a generated client.
   */
  setDashboardCharts(projectId: string, dashboardCharts: string): Promise<void> {
    return this.send<void>(
      'PATCH',
      `/api/Projects/${encodeURIComponent(projectId)}/SetDashboardCharts`,
      { dashboardCharts },
    );
  }

  private send<T>(method: 'GET' | 'PATCH', url: string, data?: unknown): Promise<T> {
    const options: AxiosRequestConfig = {
      method,
      url: this.baseUrl + url,
      headers: data
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json' },
      data: data ? JSON.stringify(data) : undefined,
    };

    return this.instance
      .request(options)
      // A non-2xx is a resolved response we want to inspect, not a transport failure.
      .catch((error: any) => {
        if (error?.response) {
          return error.response as AxiosResponse;
        }
        throw error;
      })
      .then((response: AxiosResponse) => AnalyticsService.process<T>(response));
  }

  private static process<T>(response: AxiosResponse): T {
    const { status } = response;
    const headers: { [key: string]: any } = {};
    if (response.headers && typeof response.headers === 'object') {
      Object.keys(response.headers).forEach((k) => {
        headers[k] = (response.headers as any)[k];
      });
    }

    // The shared axios instance is created with `transformResponse: (res) => res`, so bodies arrive
    // as raw strings and every client parses its own.
    const text: string = response.data;

    if (status === 200) {
      // The PATCH endpoints return Ok() with an empty body, which JSON.parse rejects.
      return (text ? JSON.parse(text) : undefined) as T;
    }
    if (status === 204) {
      return undefined as unknown as T;
    }

    throw new ApiException(
      'A server side error occurred.',
      status,
      text,
      headers,
      null,
    );
  }
}
